import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { fr } from "date-fns/locale";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, MapPin, Clock, Trophy, Users, CalendarDays, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import MatchStatsDialog from "@/components/MatchStatsDialog";

const EVENT_TYPES = [
  { value: "match", label: "Match", icon: Trophy, color: "emerald", bg: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  { value: "entrainement", label: "Entraînement", icon: Users, color: "blue", bg: "bg-blue-100 text-blue-700 border-blue-200" },
  { value: "evenement", label: "Événement", icon: CalendarDays, color: "amber", bg: "bg-amber-100 text-amber-700 border-amber-200" },
];

const CATEGORIES = [
  { value: "ALL", label: "Toutes catégories" },
  { value: "PRO", label: "Senior Pro Dames" },
  { value: "AMATEUR", label: "Senior Amateur Dames" },
  { value: "U17", label: "U17" },
  { value: "U13", label: "U13" },
];

const EMPTY = {
  event_type: "match",
  title: "",
  description: "",
  date: new Date().toISOString().slice(0, 10),
  start_time: "16:00",
  end_time: "",
  location: "",
  category: "PRO",
  opponent: "",
  status: "prevu",
};

const STATUS_LABELS = { prevu: "Prévu", termine: "Terminé", annule: "Annulé" };
const STATUS_COLORS = {
  prevu: "bg-blue-100 text-blue-700 border-blue-200",
  termine: "bg-slate-100 text-slate-700 border-slate-200",
  annule: "bg-red-100 text-red-700 border-red-200",
};

export default function Calendrier() {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("ALL");
  const [statsEvent, setStatsEvent] = useState(null);

  const load = () => api.get("/calendar").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  // Map: { 'YYYY-MM-DD': [events] }
  const byDate = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      if (filter !== "ALL" && filter !== e.event_type) return;
      map[e.date] = map[e.date] || [];
      map[e.date].push(e);
    });
    return map;
  }, [events, filter]);

  const eventDates = Object.keys(byDate).map((d) => new Date(d + "T12:00:00"));

  const selectedKey = selectedDate?.toISOString().slice(0, 10);
  const dayEvents = byDate[selectedKey] || [];

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return events
      .filter((e) => e.date >= today && (filter === "ALL" || filter === e.event_type))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(0, 8);
  }, [events, filter]);

  const startCreate = (date) => {
    setEditing(null);
    setForm({ ...EMPTY, date: date || new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };
  const startEdit = (e) => {
    setEditing(e);
    setForm({ ...EMPTY, ...e });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.patch(`/calendar/${editing.id}`, form);
        toast.success("Événement mis à jour");
      } else {
        await api.post("/calendar", form);
        toast.success("Événement créé");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet événement ?")) return;
    await api.delete(`/calendar/${id}`);
    load();
  };

  const setResult = async (e, home, away) => {
    try {
      await api.patch(`/calendar/${e.id}`, {
        result_home: Number(home), result_away: Number(away), status: "termine",
      });
      toast.success("Résultat enregistré");
      load();
    } catch (err) { toast.error("Erreur"); }
  };

  return (
    <div data-testid="calendrier-page">
      <PageHeader title="Calendrier" subtitle="Matchs, entraînements et événements du club">
        <Button onClick={() => startCreate(selectedKey)} data-testid="add-event-button"
          className="bg-emerald-600 hover:bg-emerald-700">
          <Plus className="h-4 w-4 mr-2" /> Nouvel événement
        </Button>
      </PageHeader>

      <div className="p-6 lg:p-8 space-y-6">
        {/* Type filter pills */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mr-2">Filtre :</span>
          {[{ value: "ALL", label: "Tous" }, ...EVENT_TYPES].map((t) => (
            <button
              key={t.value}
              onClick={() => setFilter(t.value)}
              data-testid={`filter-${t.value}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                filter === t.value
                  ? "bg-emerald-600 text-white border-emerald-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-emerald-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Calendar */}
          <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-4">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(d) => d && setSelectedDate(d)}
              locale={fr}
              modifiers={{ hasEvent: eventDates }}
              modifiersClassNames={{
                hasEvent: "relative font-bold text-emerald-700 after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-1 after:rounded-full after:bg-emerald-600",
              }}
              className="rounded-md"
              data-testid="event-calendar"
            />
            <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] uppercase tracking-wider text-slate-400">
              Légende
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {EVENT_TYPES.map((t) => {
                const Icon = t.icon;
                return (
                  <div key={t.value} className={`inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border ${t.bg}`}>
                    <Icon className="h-3 w-3" /> {t.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Day events */}
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                  {selectedDate?.toLocaleDateString("fr-FR", { weekday: "long" })}
                </div>
                <h3 className="font-barlow font-bold text-2xl tracking-tight">
                  {selectedDate?.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
                </h3>
              </div>
              <Badge className="bg-slate-100 text-slate-700 border-slate-200">
                {dayEvents.length} événement{dayEvents.length > 1 ? "s" : ""}
              </Badge>
            </div>

            {dayEvents.length === 0 ? (
              <div className="text-center py-12 text-sm text-slate-400">
                Aucun événement programmé ce jour.
                <div className="mt-3">
                  <Button onClick={() => startCreate(selectedKey)} variant="outline" size="sm">
                    <Plus className="h-3.5 w-3.5 mr-1" /> En programmer un
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {dayEvents.map((e) => <EventCard key={e.id} event={e} onEdit={startEdit} onDel={del} onResult={setResult} onStats={setStatsEvent} />)}
              </div>
            )}
          </div>
        </div>

        {/* Upcoming */}
        <div className="bg-white border border-slate-200 rounded-md p-6">
          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">À venir</div>
          <h3 className="font-barlow font-bold text-xl tracking-tight mb-4">Prochains événements</h3>
          {upcoming.length === 0 ? (
            <div className="text-sm text-slate-400 py-6 text-center">Aucun événement à venir.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {upcoming.map((e) => <UpcomingCard key={e.id} event={e} onClick={() => setSelectedDate(new Date(e.date + "T12:00:00"))} />)}
            </div>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="event-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">{editing ? "Modifier" : "Nouvel événement"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <SelectField label="Type" value={form.event_type}
              onChange={(v) => setForm({ ...form, event_type: v })}
              options={EVENT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
              testid="event-type" />
            <SelectField label="Catégorie" value={form.category || "ALL"}
              onChange={(v) => setForm({ ...form, category: v })}
              options={CATEGORIES} testid="event-category" />
            <div className="col-span-2">
              <Field label="Titre" value={form.title} onChange={(v) => setForm({ ...form, title: v })} required testid="event-title" />
            </div>
            <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="event-date" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="Heure début" type="time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testid="event-start" />
              <Field label="Heure fin" type="time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testid="event-end" />
            </div>
            <Field label="Lieu" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testid="event-location" />
            {form.event_type === "match" && (
              <Field label="Adversaire" value={form.opponent} onChange={(v) => setForm({ ...form, opponent: v })} testid="event-opponent" />
            )}
            <SelectField label="Statut" value={form.status}
              onChange={(v) => setForm({ ...form, status: v })}
              options={[
                { value: "prevu", label: "Prévu" },
                { value: "termine", label: "Terminé" },
                { value: "annule", label: "Annulé" },
              ]} testid="event-status" />
            <div className="col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-1 block">Description</Label>
              <Textarea data-testid="event-description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-event-button" className="bg-emerald-600 hover:bg-emerald-700">
              {editing ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MatchStatsDialog event={statsEvent} open={!!statsEvent} onClose={() => setStatsEvent(null)} />
    </div>
  );
}

function EventCard({ event, onEdit, onDel, onResult, onStats }) {
  const typ = EVENT_TYPES.find((t) => t.value === event.event_type);
  const Icon = typ?.icon || CalendarDays;
  const [resultMode, setResultMode] = useState(false);
  const [home, setHome] = useState(event.result_home ?? "");
  const [away, setAway] = useState(event.result_away ?? "");

  return (
    <div className="border border-slate-200 rounded-md p-4 hover:border-emerald-300 transition-colors" data-testid={`event-${event.id}`}>
      <div className="flex items-start gap-3">
        <div className={`h-9 w-9 rounded-md border flex items-center justify-center shrink-0 ${typ?.bg}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-barlow font-bold text-lg tracking-tight">{event.title}</div>
            <Badge className={STATUS_COLORS[event.status]}>{STATUS_LABELS[event.status]}</Badge>
            {event.category && event.category !== "ALL" && (
              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">{event.category}</Badge>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-slate-600">
            {event.start_time && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {event.start_time}{event.end_time ? ` – ${event.end_time}` : ""}</span>}
            {event.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" /> {event.location}</span>}
          </div>
          {event.event_type === "match" && (
            <div className="mt-2 text-sm">
              <span className="font-semibold">AMAL VBC</span>
              {event.result_home != null && event.result_away != null ? (
                <span className="mx-2 font-barlow font-bold text-2xl text-emerald-700">
                  {event.result_home} — {event.result_away}
                </span>
              ) : (
                <span className="mx-2 text-slate-400">vs</span>
              )}
              <span className="font-semibold">{event.opponent || "—"}</span>
            </div>
          )}
          {event.description && <div className="text-xs text-slate-500 mt-2">{event.description}</div>}

          {resultMode && event.event_type === "match" && (
            <div className="flex items-center gap-2 mt-3 p-2 bg-slate-50 rounded-md">
              <input type="number" placeholder="Nous" value={home} onChange={(e) => setHome(e.target.value)}
                className="h-8 w-16 border rounded px-2 text-sm" data-testid={`result-home-${event.id}`} />
              <span>—</span>
              <input type="number" placeholder="Adv" value={away} onChange={(e) => setAway(e.target.value)}
                className="h-8 w-16 border rounded px-2 text-sm" data-testid={`result-away-${event.id}`} />
              <Button size="sm" onClick={() => { onResult(event, home, away); setResultMode(false); }}
                className="bg-emerald-600 hover:bg-emerald-700 h-8" data-testid={`save-result-${event.id}`}>
                Valider
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setResultMode(false)} className="h-8">Annuler</Button>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          {event.event_type === "match" && event.status !== "annule" && !resultMode && (
            <>
              <Button size="sm" variant="outline" onClick={() => setResultMode(true)}
                className="text-xs h-7" data-testid={`set-result-${event.id}`}>
                Score
              </Button>
              <Button size="sm" variant="outline" onClick={() => onStats(event)}
                className="text-xs h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid={`stats-event-${event.id}`}>
                Performances
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => onEdit(event)} className="h-7 text-xs"
            data-testid={`edit-event-${event.id}`}>Modifier</Button>
          <Button size="sm" variant="ghost" onClick={() => onDel(event.id)}
            className="text-red-600 hover:bg-red-50 h-7 w-7 p-0" data-testid={`delete-event-${event.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function UpcomingCard({ event, onClick }) {
  const typ = EVENT_TYPES.find((t) => t.value === event.event_type);
  const Icon = typ?.icon || CalendarDays;
  return (
    <button onClick={onClick} className="text-left border border-slate-200 rounded-md p-3 hover:border-emerald-300 transition-colors w-full"
      data-testid={`upcoming-${event.id}`}>
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-md border flex items-center justify-center shrink-0 ${typ?.bg}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{event.title}</div>
          <div className="text-xs text-slate-500">{formatDate(event.date)} {event.start_time && `• ${event.start_time}`}</div>
        </div>
      </div>
    </button>
  );
}
